import {isEnterprise} from '@github-ui/runtime-environment'
import {CircleSlashIcon} from '@primer/octicons-react'

import {ERRORS} from './constants/errors'

type ItemGroupVariant = 'subtle' | 'filled'

export type ItemGroup = {
  groupId: string
  header?: {
    title: string
    variant?: ItemGroupVariant
  }
}

export const noMatchesItem = {
  leadingVisual: CircleSlashIcon,
  text: ERRORS.noMatches,
  disabled: true,
  selected: undefined, // hide checkbox
  key: 'no-matches',
  id: 'no-matches',
}

export const noResultsItem = {
  leadingVisual: CircleSlashIcon,
  text: ERRORS.noResults,
  disabled: true,
  selected: undefined, // hide checkbox
  key: 'no-results',
  id: 'no-results',
}

export const maybeIssueUrl = (url: string): boolean => {
  try {
    // eslint-disable-next-line no-restricted-syntax
    new URL(url)
  } catch {
    return false
  }

  return new RegExp(/^.+\/issues\/\d+$/).test(url)
}

export const maybePullRequestUrl = (url: string): boolean => {
  try {
    // eslint-disable-next-line no-restricted-syntax
    new URL(url)
  } catch {
    return false
  }

  return new RegExp(/^.+\/pull\/\d+$/).test(url)
}

// Generates a set of queries which scope to an owner, and prioritizes issues based on interaction
// The IssuePickerInternal orders the queried issues and is the source for this order of this priority
export const getIssueSearchQueries = (owner = '', query = '', repoNameWithOwner = '') => {
  const ownerPart = owner && `owner:${owner.trim()} `
  const repoPart = repoNameWithOwner && `repo:${repoNameWithOwner.trim()} `
  const queryPart = query && `${query.trim()}`

  // Although these queries are using the `... on Issue` type condition,
  // PR results are still inlcuded in the response in an empty object.
  // `is:issue` ensures that only issue nodes are returned.
  const issuePart = 'is:issue '
  const queryIsUrl = maybeIssueUrl(query)

  // By default, search within the issue title as it's the most relevant option.
  let inPart = 'in:title'

  // If the query is a number (123 or #123), then we should search for the issue by number as well.
  const maybeNumber = queryPart && queryPart.charAt(0) === '#' ? queryPart.slice(1) : queryPart
  if (maybeNumber && !isNaN(Number(maybeNumber))) {
    inPart += ' in:number'
  }

  const sharedQuery = `${ownerPart}${repoPart}${issuePart}${inPart}`

  return {
    commenters: `${sharedQuery} commenter:@me ${queryPart}`.trim(),
    mentions: `${sharedQuery} mentions:@me ${queryPart}`.trim(),
    assignee: `${sharedQuery} assignee:@me ${queryPart}`.trim(),
    author: `${sharedQuery} author:@me ${queryPart}`.trim(),
    other: `${sharedQuery} ${queryPart}`.trim(),
    resource: queryIsUrl ? encodeURI(query) : '',
    queryIsUrl,
  }
}

const MAX_SEMANTIC_TITLE_LENGTH = 256

// Strip these qualifiers from titles so one like `is:closed crashes` can't silently filter or
// rescope the suggestions. An allowlist (not a generic `word:` strip) keeps ordinary titles like
// `TypeError: cannot read property 'x'` intact. Mirrors `Search::Queries::IssueQuery.field_list`
// plus `in`; a qualifier missing here is one a title can smuggle into the query.
const SEARCH_QUALIFIERS =
  `archived assignee author base closed comments commenter created draft field has head in interactions involves is issue_type label lang language linked mentions merged milestone no org owner parent-issue project reactions reason repo review review-involves review-requested reviewable-state reviewed-by sort state status sub-issue suggestions team team-review-requested team-review-requested-user type updated user user-review-requested`.split(
    ' ',
  )
const SEARCH_QUALIFIER_PATTERN = new RegExp(String.raw`\b(?:${SEARCH_QUALIFIERS.join('|')}):\S*`, 'gi')

const sanitizeSemanticTitle = (title: string) =>
  title
    .replace(SEARCH_QUALIFIER_PATTERN, ' ')
    .replace(/["']/g, ' ')
    .replace(/(^|\s)-+/g, '$1')
    .replace(/\s+/g, ' ')
    .trim()

// Truncate on a word boundary so the embedding model never receives a half-word.
const truncateOnWordBoundary = (text: string, maxLength: number) => {
  if (text.length <= maxLength) return text
  // `slice` cuts on UTF-16 units and can leave an unpaired surrogate (a split emoji); drop it.
  const head = text.slice(0, maxLength).replace(/[\uD800-\uDBFF]$/, '')
  // A cut that already lands on whitespace ends on a complete word; keep it as-is.
  if (/\s/.test(text.charAt(maxLength))) return head.trim()
  return head.replace(/\s\S*$/, '').trim()
}

// Builds a semantic search query from an issue title, scoped to `repo:` (or `owner:` when no repo is
// selected) to bound cost, mirroring `getIssueSearchQueries`.
export const getSemanticallySimilarQuery = (owner = '', title = '', repoNameWithOwner = '') => {
  const sanitizedTitle = truncateOnWordBoundary(sanitizeSemanticTitle(title), MAX_SEMANTIC_TITLE_LENGTH)
  if (!sanitizedTitle) return ''
  const scope = repoNameWithOwner ? `repo:${repoNameWithOwner.trim()} ` : owner ? `owner:${owner.trim()} ` : ''
  return `${scope}is:issue ${sanitizedTitle}`.trim()
}

// The hybrid search enum is dotcom-only and absent from the GHES schema, so only send it on dotcom.
export const getSemanticSearchType = (): 'ISSUE_HYBRID' | undefined => (isEnterprise() ? undefined : 'ISSUE_HYBRID')

export type Unpacked<T> = T extends Array<infer U> ? U : T
